import { useState, useEffect, useRef } from 'react'
import { Box, Typography, Stack, Tooltip, Badge } from '@mui/material'
import { useNavigate, useLocation } from 'react-router'
import { observer } from 'mobx-react-lite'
import {
  DashboardOutlined as DashboardIcon,
  LogoutOutlined as LogoutIcon,
  DynamicForm as LeadFormsIcon,
  CalendarTodayOutlined as CalendarIcon,
  AssignmentOutlined as TasksIcon,
  HistoryOutlined as ActivityIcon,
  SchemaOutlined as WorkflowsIcon,
  TrendingUpOutlined as SalesIcon,
  HubOutlined as ChannelsSvgIcon,
  AutorenewOutlined as RetentionIcon,
  VerifiedUserOutlined as CustomerVerifyIcon,
  ShoppingCartOutlined as OrderVerifyIcon,
  ReceiptLongOutlined as RecentOrdersIcon,
} from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import { authStore } from '../../lib/stores/authStore'
import { tasksApi } from '../../lib/api/tasksApi'
import { usePermission } from '../../lib/hooks/usePermission'
import {
  DealsSvgIcon,
  LeadsSvgIcon,
  ContactsSvgIcon,
  CompanySvgIcon,
  SettingsSvgIcon,
} from '../../assests/sidebar-icon/SidebarIcons'
import { themeColors } from './theme'
import { ConfirmDialog } from '../../components/shared/ConfirmDialog'

const SIDEBAR_COLLAPSED_KEY = 'crm_sidebar_collapsed'

// ── Custom Collapse/Expand Icons (Atlassian style) ───────────────────────────
const CollapseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M9 3v18" />
    <path d="m16 15-3-3 3-3" />
  </svg>
)

const ExpandIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M9 3v18" />
    <path d="m13 9 3 3-3 3" />
  </svg>
)

interface NavItem {
  icon: React.ReactNode
  label: string
  path: string
  requirePerm?: string
}

const navItems: NavItem[] = [
  { icon: <DashboardIcon />, label: 'Dashboard', path: '/' },
  { icon: <LeadsSvgIcon />, label: 'Leads', path: '/leads', requirePerm: 'lead:read' },
  { icon: <DealsSvgIcon />, label: 'Deals', path: '/deals' },
  { icon: <ContactsSvgIcon />, label: 'Contacts', path: '/contacts' },
  { icon: <CompanySvgIcon />, label: 'Companies', path: '/customers' },
  { icon: <CalendarIcon />, label: 'Calendar', path: '/calendar' },
  { icon: <TasksIcon />, label: 'Tasks', path: '/tasks' },
  { icon: <ActivityIcon />, label: 'Activity', path: '/activity' },
  { icon: <WorkflowsIcon />, label: 'Workflows', path: '/workflows' },
  { icon: <SalesIcon />, label: 'Sales', path: '/sales' },
  { icon: <RetentionIcon />, label: 'Customer Retention', path: '/customer-retention' },
  { icon: <RecentOrdersIcon />, label: 'Recency Dashboard', path: '/recency-orders' },
  { icon: <CustomerVerifyIcon />, label: 'Customer Verification', path: '/customer-verification' },
  { icon: <OrderVerifyIcon />, label: 'Order Verification', path: '/order-verification' },
  { icon: <ChannelsSvgIcon />, label: 'Channels', path: '/channels', requirePerm: 'channel:read' },
  { icon: <LeadFormsIcon />, label: 'Custom Forms', path: '/custom-forms' },
  { icon: <SettingsSvgIcon />, label: 'Settings', path: '/settings' },
]

export const Sidebar = observer(() => {
  const navigate = useNavigate()
  const location = useLocation()
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(() => {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'
  })
  const [width, setWidth] = useState<number>(() => {
    const saved = localStorage.getItem('crm_sidebar_width')
    return saved ? Number(saved) : 240
  })
  const [lastExpandedWidth, setLastExpandedWidth] = useState<number>(() => {
    const saved = localStorage.getItem('crm_sidebar_width')
    return saved ? Number(saved) : 240
  })
  const [isDragging, setIsDragging] = useState(false)
  // isDragActive is true only once the mouse has actually moved during a drag.
  // This prevents a single click on the handle from causing any visual change.
  const [isDragActive, setIsDragActive] = useState(false)

  // Refs so event-handler closures always read the latest values
  const dragStartXRef       = useRef(0)
  const hasDraggedRef       = useRef(false)
  const lastExpandedWidthRef = useRef(lastExpandedWidth)
  useEffect(() => { lastExpandedWidthRef.current = lastExpandedWidth }, [lastExpandedWidth])

  const { canViewMenu } = usePermission()

  const { data: tasksSummary } = useQuery({
    queryKey: ['tasks', 'summary'],
    queryFn: () => tasksApi.getMySummary(),
    staleTime: 60_000,
    retry: false,
    enabled: !!authStore.user,
  })

  const pendingCount = tasksSummary?.pendingCount ?? 0

  const toggleCollapse = () => {
    setIsCollapsed(prev => {
      const next = !prev
      if (!next) {
        // Expanding — restore last known width
        setWidth(lastExpandedWidthRef.current)
      } else {
        // Collapsing — save current width
        setLastExpandedWidth(width)
        lastExpandedWidthRef.current = width
      }
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next))
      return next
    })
  }

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/'
    if (path === '/sales') {
      const subPaths = ['/customer-retention', '/customer-verification', '/order-verification']
      if (subPaths.some(p => location.pathname.startsWith(p))) {
        return false
      }
    }
    return location.pathname.startsWith(path)
  }

  useEffect(() => {
    if (!isDragging) return

    const handleMouseMove = (e: MouseEvent) => {
      // Only start responding after the mouse has moved more than 4px —
      // this makes single clicks on the handle a complete no-op visually.
      if (!hasDraggedRef.current && Math.abs(e.clientX - dragStartXRef.current) > 4) {
        hasDraggedRef.current = true
        setIsDragActive(true)
      }
      if (hasDraggedRef.current) {
        const newWidth = Math.max(72, Math.min(e.clientX, 480))
        setWidth(newWidth)
      }
    }

    const handleMouseUp = (e: MouseEvent) => {
      if (hasDraggedRef.current) {
        const raw = e.clientX
        if (raw < 160) {
          // Dragged below threshold — collapse and remember last good width
          const restore = lastExpandedWidthRef.current >= 200 ? lastExpandedWidthRef.current : 240
          setIsCollapsed(true)
          setWidth(restore)
          localStorage.setItem(SIDEBAR_COLLAPSED_KEY, 'true')
        } else {
          // Dragged to an expanded position
          const finalWidth = Math.max(200, Math.min(raw, 480))
          setWidth(finalWidth)
          setLastExpandedWidth(finalWidth)
          lastExpandedWidthRef.current = finalWidth
          setIsCollapsed(false)
          localStorage.setItem('crm_sidebar_width', String(finalWidth))
          localStorage.setItem(SIDEBAR_COLLAPSED_KEY, 'false')
        }
      }
      // Always reset drag state
      hasDraggedRef.current = false
      setIsDragActive(false)
      setIsDragging(false)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging])

  const startResizing = (e: React.MouseEvent) => {
    e.preventDefault()
    dragStartXRef.current = e.clientX
    hasDraggedRef.current = false
    setIsDragging(true)
  }

  const handleLogoutClick = () => {
    setIsLogoutConfirmOpen(true)
  }

  const handleConfirmLogout = async () => {
    setIsLogoutConfirmOpen(false)
    await authStore.logout()
  }

  // Filter visible items
  const visibleItems = navItems.filter(item => canViewMenu(item.path))

  return (
    <Box
      sx={{
        // isDragActive drives the live preview so single clicks don't cause any flash
        width: isDragActive ? width : (isCollapsed ? 72 : width),
        minWidth: isDragActive ? width : (isCollapsed ? 72 : width),
        backgroundColor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        maxHeight: '100vh',
        flexShrink: 0,
        borderRight: '1px solid #ebebf5',
        overflow: 'visible',
        position: 'relative',
        transition: isDragActive ? 'none' : 'width 0.2s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      {/* Header / Brand (Matched to 56px Topbar height for perfect border line alignment) */}
      <Box
        sx={{
          width: '100%',
          height: 56,
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCollapsed ? 'center' : 'space-between',
          px: isCollapsed ? 1 : 2.25,
          borderBottom: '1px solid #ebebf5',
          flexShrink: 0,
          boxSizing: 'border-box',
        }}
      >
        {!isCollapsed ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: '6px',
                  background: themeColors.primary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(75, 48, 130, 0.22)',
                }}
              >
                <Typography sx={{ color: '#ffffff', fontWeight: 900, fontSize: '15px', lineHeight: 1 }}>
                  M
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: '14.5px',
                  fontWeight: 800,
                  color: '#1e293b',
                  letterSpacing: '0.75px',
                  textTransform: 'uppercase',
                  lineHeight: 1,
                }}
              >
                MOMENTUM
              </Typography>
            </Box>
            <Box
              onClick={toggleCollapse}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                borderRadius: '6px',
                cursor: 'pointer',
                color: '#64748b',
                transition: 'all 0.15s ease',
                '&:hover': {
                  bgcolor: '#f1f5f9',
                  color: themeColors.primary,
                },
              }}
            >
              <CollapseIcon />
            </Box>
          </>
        ) : (
          <Box
            onClick={toggleCollapse}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: '8px',
              cursor: 'pointer',
              color: '#64748b',
              transition: 'all 0.15s ease',
              '&:hover': {
                bgcolor: '#f1f5f9',
                color: themeColors.primary,
              },
            }}
          >
            <ExpandIcon />
          </Box>
        )}
      </Box>

      {/* Nav List */}
      <Box
        sx={{
          flex: 1,
          px: isCollapsed ? '12px' : '16px',
          pt: '16px',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          overflowX: 'hidden',
          minHeight: 0,
          '&::-webkit-scrollbar': {
            width: '5px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#cbd5e1',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#94a3b8',
          },
        }}
      >
        <Stack spacing={0.5}>
          {visibleItems.map(item => {
            const active = isActive(item.path)
            const isTasks = item.label === 'Tasks'

            const itemContent = (
              <Box
                onClick={() => navigate(item.path)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: isCollapsed ? 'center' : 'flex-start',
                  gap: isCollapsed ? 0 : '10px',
                  width: isCollapsed ? 40 : 'auto',
                  height: isCollapsed ? 40 : 'auto',
                  mx: isCollapsed ? 'auto' : 0,
                  pl: isCollapsed ? 0 : '12px',
                  pr: isCollapsed ? 0 : '12px',
                  py: isCollapsed ? 0 : '9px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  bgcolor: active ? themeColors.primary : 'transparent',
                  color: active ? '#ffffff' : '#64748b',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    bgcolor: active ? themeColors.primary : '#f8fafc',
                    color: active ? '#ffffff' : themeColors.primary,
                    '& svg': {
                      color: active ? '#ffffff' : themeColors.primary,
                    },
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 22,
                    height: 22,
                    '& svg': {
                      fontSize: '20px',
                      color: active ? '#ffffff' : '#94a3b8',
                      transition: 'color 0.15s ease',
                    },
                  }}
                >
                  {isCollapsed ? (
                    <Badge
                      variant="dot"
                      color="error"
                      invisible={!isTasks || pendingCount === 0}
                      sx={{ '& .MuiBadge-badge': { right: -2, top: -2 } }}
                    >
                      {item.icon}
                    </Badge>
                  ) : (
                    item.icon
                  )}
                </Box>

                {!isCollapsed && (
                  <>
                    <Typography
                      sx={{
                        fontSize: '13.5px',
                        fontWeight: active ? 600 : 500,
                        lineHeight: 1,
                        flex: 1,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        transition: 'color 0.15s ease',
                      }}
                    >
                      {item.label}
                    </Typography>

                    {isTasks && pendingCount > 0 && (
                      <Box
                        sx={{
                          minWidth: 18,
                          height: 18,
                          borderRadius: '9px',
                          bgcolor: active ? 'rgba(255, 255, 255, 0.25)' : '#ef4444',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          px: '5px',
                        }}
                      >
                        <Typography sx={{ fontSize: '10px', fontWeight: 700, lineHeight: 1 }}>
                          {pendingCount > 99 ? '99+' : pendingCount}
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            )

            return isCollapsed ? (
              <Tooltip key={item.path} title={item.label} placement="right" arrow>
                {itemContent}
              </Tooltip>
            ) : (
              <Box key={item.path}>{itemContent}</Box>
            )
          })}
        </Stack>
      </Box>

      {/* Sign Out */}
      <Box sx={{ px: isCollapsed ? '12px' : '16px', pb: '24px', pt: '12px', borderTop: '1px solid #f1f5f9', flexShrink: 0 }}>
        {isCollapsed ? (
          <Tooltip title="Sign Out" placement="right" arrow>
            <Box
              onClick={handleLogoutClick}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: '8px',
                cursor: 'pointer',
                border: '1px solid #fee2e2',
                backgroundColor: '#fef2f2',
                transition: 'all 0.15s ease',
                '&:hover': {
                  backgroundColor: '#fecaca',
                  borderColor: '#fca5a5',
                  '& svg': { color: '#dc2626' },
                },
                mx: 'auto',
              }}
            >
              <LogoutIcon sx={{ fontSize: '20px', color: '#ef4444' }} />
            </Box>
          </Tooltip>
        ) : (
          <Box
            onClick={handleLogoutClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              px: '14px',
              py: '9px',
              borderRadius: '8px',
              cursor: 'pointer',
              border: '1px solid #fee2e2',
              backgroundColor: '#fef2f2',
              transition: 'all 0.15s ease',
              '&:hover': {
                backgroundColor: '#fecaca',
                borderColor: '#fca5a5',
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                '& svg': { fontSize: '20px', color: '#ef4444' },
              }}
            >
              <LogoutIcon />
            </Box>
            <Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: '#ef4444', lineHeight: 1 }}>
              Sign Out
            </Typography>
          </Box>
        )}
      </Box>

      <ConfirmDialog
        open={isLogoutConfirmOpen}
        title="Sign Out"
        description="Are you sure you want to sign out?"
        confirmLabel="Sign Out"
        confirmColor="error"
        onClose={() => setIsLogoutConfirmOpen(false)}
        onConfirm={handleConfirmLogout}
      />

      {/* Resizing Handle — always present; dragging right from collapsed expands the sidebar */}
      <Box
        onMouseDown={startResizing}
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          right: -3,
          width: 6,
          cursor: 'col-resize',
          zIndex: 100,
          '&:hover .drag-line, &:active .drag-line': {
            bgcolor: themeColors.primary,
          },
        }}
      >
        <Box
          className="drag-line"
          sx={{
            width: '2px',
            height: '100%',
            bgcolor: isDragging ? themeColors.primary : 'transparent',
            transition: 'background-color 0.15s ease',
          }}
        />
      </Box>

      {isDragActive && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            cursor: 'col-resize',
            zIndex: 9999,
            userSelect: 'none',
          }}
        />
      )}
    </Box>
  )
})
